import { ComponentProps, forwardRef, PropsWithChildren } from 'react';

import { withEventCatcher } from '@repo/event-processor';
import IconClose from '@repo/ui-icons/IconClose';
import cn from 'classnames';

import { BaseComponentProps } from '../../../types/common';
import { CircleButton } from '../Button';
import { H6, H7 } from '../Typography';

import styles from './Tag.module.css';

const getLabelColor = ({
  isCritical,
  isActive,
}: {
  isCritical: boolean;
  isActive: boolean;
}) => {
  if (isCritical) {
    return 'critical';
  }

  return isActive ? 'default' : 'secondary';
};

// The two props can't be used together
type TagClickProps =
  | {
      /**
       * Callback function when the tag is clicked
       */
      onClick?: () => void;
      onClose?: never;
      isCritical?: never;
    }
  | {
      onClick?: never;
      /**
       * Callback function when the close button is clicked. It can NOT be combined with onClick
       */
      onClose?: () => void;
      /**
       * Whether the tag is invalid
       * This props adds the aria-invalid attribute to the tag and makes it look like a critical tag
       */
      isCritical?: boolean;
    };

export type TagProps = PropsWithChildren<BaseComponentProps> & {
  /**
   * Size of the tag
   */
  size?: 's' | 'xs';
  /**
   * Whether the tag is in active state
   * This props adds the aria-selected attribute to the tag and makes it look like an active tag
   */
  isActive?: boolean;
  /**
   * This props is used to prevent the tag and text from overflowing
   */
  preventTextOverflow?: boolean;
} & TagClickProps;

/**
 * The tag component is used to display a small label or status information.
 */
const _Tag: React.FC<TagProps> = forwardRef(
  (
    {
      children,
      isCritical = false,
      size = 's',
      isActive = false,
      onClose,
      onClick,
      dataTestId,
      preventTextOverflow = false,
    }: TagProps,
    ref
  ) => {
    const Element = onClick ? 'button' : 'div';
    const Label = size === 'xs' ? H7 : H6;
    const labelColor = getLabelColor({ isCritical, isActive });

    return (
      <Element
        ref={ref as React.Ref<HTMLDivElement & HTMLButtonElement>}
        aria-invalid={isCritical}
        aria-selected={isCritical ? undefined : isActive}
        className={cn(
          styles.tag,
          styles[`size-${size}`],
          styles[
            `type-${onClick ? 'clickable' : onClose ? 'removable' : 'default'}`
          ],
          { [styles['tag-overflow-ellipsis']]: preventTextOverflow }
        )}
        data-testid={dataTestId}
        onClick={onClose ? undefined : onClick}
      >
        <Label
          as="span"
          color={labelColor}
          textOverflowEllipsis={preventTextOverflow}
        >
          {children}
        </Label>
        {onClose && (
          <CircleButton
            ariaLabel="Remove Tag"
            icon={IconClose}
            isCritical={isCritical}
            name="removeTagButton"
            size="xxs"
            variant="ghost"
            onClick={onClose}
          />
        )}
      </Element>
    );
  }
);

_Tag.displayName = 'Tag';

export const Tag = withEventCatcher<
  ComponentProps<typeof _Tag>,
  HTMLDivElement,
  false
>(_Tag);
